import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import { Moon, Sun, Loader2 } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const SubmitReview: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { search } = useLocation();
  const qrCode = new URLSearchParams(search).get('code');
  const { setTheme } = useTheme();

  useEffect(() => {
    const redirectToReviewForm = async () => {
      if (!qrCode) {
        toast.error("You must scan a QR code to submit a review");
        navigate("/");
        return;
      }

      try {
        // Check if this is an old QR code from the legacy system
        const { data: qrData, error: qrError } = await supabase
          .from('qr_codes')
          .select('business_id, name')
          .eq('unique_url_path', qrCode.trim())
          .single();

        if (qrError && qrError.code !== 'PGRST116') {
          throw qrError;
        }

        if (qrData) {
          // This is a legacy QR code - find the default review form for this business
          const { data: formData, error: formError } = await supabase
            .from('review_forms')
            .select('id')
            .eq('business_id', qrData.business_id)
            .eq('active_status', true)
            .order('created_at', { ascending: true })
            .limit(1)
            .single();

          if (formError || !formData) {
            toast.error("No active review form found for this business", {
              description: "Please contact the business to resolve this issue."
            });
            navigate("/");
            return;
          }

          // Redirect to the new review form system
          navigate(`/review/${formData.id}`, { replace: true });
          return;
        }

        // If we get here, the QR code doesn't exist
        toast.error("Invalid QR code", {
          description: "This QR code doesn't match any business in our system."
        });
        navigate("/");

      } catch (error: any) {
        console.error("Error processing QR code:", error);
        toast.error("Error processing QR code", {
          description: error.message || "Please try again."
        });
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    redirectToReviewForm();
  }, [qrCode, navigate]);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <div className="text-center">
            <div className="text-lg font-medium">Processing QR Code...</div>
            <div className="text-sm text-muted-foreground mt-1">Redirecting to review form</div>
          </div>
        </div>
      </div>
    );
  }

  // This should not render since we always navigate away
  return null;
};

export default SubmitReview;